import React, { useState } from "react";
import toast from "react-hot-toast";
import { createRazorpayOrder, verifyRazorpayPayment } from "../services/api";
import { activeCurrencySymbol } from "../utils/currency";
import { loadRazorpayCheckout, RazorpayResponse } from "../utils/razorpay";

interface RazorpayPaymentProps {
  pricing: number;
  restaurantId: string;
  phone?: string;
  handleOrder: (paymentMethod: string, transactionId?: string) => void;
}

/** Card/UPI payment through the restaurant's own Razorpay account. */
export default function RazorpayPayment({
  pricing,
  restaurantId,
  phone,
  handleOrder,
}: RazorpayPaymentProps) {
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      if (!(await loadRazorpayCheckout())) {
        toast.error("Could not load Razorpay. Check your internet connection.");
        return;
      }

      const { data } = await createRazorpayOrder(restaurantId, pricing);
      const { orderId, amount, currency, keyId } = data.data;

      const checkout = new window.Razorpay({
        key: keyId,
        order_id: orderId,
        amount,
        currency,
        name: "Order Payment",
        prefill: phone ? { contact: phone } : undefined,
        handler: async (response: RazorpayResponse) => {
          try {
            await verifyRazorpayPayment({ hotelId: restaurantId, ...response });
            toast.success("Payment successful!");
            handleOrder("razorpay", response.razorpay_payment_id);
          } catch (err: any) {
            toast.error(err.response?.data?.error || "Payment verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      checkout.on("payment.failed", (resp: any) => {
        toast.error(resp?.error?.description || "Payment failed");
      });
      checkout.open();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Could not start payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-lg p-4">
      <button
        type="button"
        onClick={pay}
        disabled={loading}
        className="w-full rounded-md bg-black px-4 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
      >
        {loading ? "Opening Razorpay..." : `PAY ${activeCurrencySymbol()}${pricing.toFixed(2)}`}
      </button>
      <p className="mt-2 text-center text-xs text-gray-500">UPI, cards, netbanking and wallets via Razorpay</p>
    </div>
  );
}
